import React from 'react';
import useButtonClassName, { ButtonVariantProps } from './useButtonClassName';

type ButtonVariantKeys = keyof ButtonVariantProps;

export type AnchorButtonProps<C extends React.ElementType = 'a'> = {
  as?: C;
  disabled?: boolean;
  openInNewTab?: boolean;
} & ButtonVariantProps &
  Omit<
    React.ComponentPropsWithoutRef<C>,
    ButtonVariantKeys | 'disabled' | 'openInNewTab' | 'as'
  >;

const AnchorButton = <C extends React.ElementType = 'a'>(
  props: AnchorButtonProps<C>
) => {
  const {
    as,
    variant,
    size,
    colour,
    shape,
    className,
    disabled,
    children,
    linkColour,
    openInNewTab = false,
    ...rest
  } = props;

  const Component = as || 'a';

  const variantClassName = useButtonClassName({
    variant,
    size,
    colour,
    shape,
    linkColour,
  });

  // Anchor Links can't be disabled, render an aria-disabled span instead!
  if (disabled) {
    return (
      <span
        className={variantClassName + (className ? ' ' + className : '')}
        aria-disabled="true"
      >
        {children}
      </span>
    );
  }

  return (
    <Component
      className={variantClassName + (className ? ' ' + className : '')}
      target={openInNewTab ? '_blank' : undefined}
      rel={openInNewTab ? 'noopener noreferrer' : undefined}
      {...rest}
    >
      {children}
    </Component>
  );
};

export default AnchorButton;
